import React, { useState } from 'react';
import { Helmet } from 'react-helmet';

const JsonValidator = () => {
  const [input, setInput] = useState('');
  const [result, setResult] = useState({ valid: false, checked: false, error: '', formatted: '' });
  const [indent, setIndent] = useState(2);

  // Validate JSON
  const validateJson = () => {
    if (!input.trim()) {
      setResult({ valid: false, checked: true, error: 'Please enter JSON to validate', formatted: '' });
      return;
    }

    try {
      // Parse JSON to validate structure
      const parsed = JSON.parse(input);

      // If parsing succeeds, format it with the selected indentation
      const formatted = JSON.stringify(parsed, null, indent);

      setResult({
        valid: true,
        checked: true,
        error: '',
        formatted: formatted
      });
    } catch (err) {
      // If parsing fails, show the error
      setResult({
        valid: false,
        checked: true,
        error: `Invalid JSON: ${err.message}`,
        formatted: ''
      });
    }
  };

  // Format JSON without validation (keeping any errors)
  const formatJson = () => {
    try {
      const parsed = JSON.parse(input);
      setInput(JSON.stringify(parsed, null, indent));
    } catch (err) {
      // Do nothing if input is not valid JSON
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Clear all fields
  const clearAll = () => {
    setInput('');
    setResult({ valid: false, checked: false, error: '', formatted: '' });
  };

  return (

    <>
      <Helmet>
        <title>JSON Validator Tool</title>
        <meta name="description" content="Validate and format your JSON quickly with this tool." />
        <meta property="og:title" content="JSON Validator Tool" />
        <meta property="og:description" content="Validate and format your JSON quickly with this tool." />
        <meta property="og:image" content="url_to_image" />
      </Helmet>
      <main className="flex-grow px-4 py-8">
        <div className="max-w-screen-2xl mx-auto">
          {/* Tool Interface */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Left Panel - Input */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-700">JSON Input</h2>

                <div className="flex space-x-2">
                  <button
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                    onClick={formatJson}
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path>
                    </svg>
                    Format
                  </button>
                  <button
                    className="text-sm text-red-600 hover:text-red-800 flex items-center"
                    onClick={clearAll}
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                    </svg>
                    Clear
                  </button>
                </div>
              </div>

              {/* JSON Input */}
              <textarea
                className="w-full h-64 font-mono text-sm p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Paste your JSON here..."
              />

              {/* Actions */}
              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <button
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition duration-150"
                    onClick={validateJson}
                  >
                    Validate JSON
                  </button>

                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-gray-600">Indent:</label>
                    <select
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                      value={indent}
                      onChange={(e) => setIndent(Number(e.target.value))}
                    >
                      <option value="0">None</option>
                      <option value="2">2 spaces</option>
                      <option value="4">4 spaces</option>
                      <option value="8">8 spaces</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel - Output */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-700">Validation Result</h2>

                {/* Copy Button */}
                {result.formatted && (
                  <button
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                    onClick={() => copyToClipboard(result.formatted)}
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"></path>
                    </svg>
                    Copy
                  </button>
                )}
              </div>

              {/* Validation Status */}
              {result.checked && (
                <div className={`p-3 rounded-md mb-4 ${result.valid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                  <div className="flex items-center">
                    {result.valid ? (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        <span>Valid JSON!</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                        <span>{result.error}</span>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* JSON Output */}
              {result.formatted && (
                <div className="w-full h-64 font-mono text-sm p-3 border border-gray-300 rounded-md bg-gray-50 overflow-auto">
                  <pre className="text-gray-800">{result.formatted}</pre>
                </div>
              )}

              {/* Help Information */}
              {!result.checked && (
                <div className="text-gray-500 space-y-4">
                  <div className="p-4 border border-gray-200 rounded-md bg-gray-50">
                    <h3 className="font-medium text-gray-700 mb-2">What is JSON?</h3>
                    <p>JSON (JavaScript Object Notation) is a lightweight data-interchange format that is easy for humans to read and write and easy for machines to parse and generate.</p>
                  </div>

                  <div className="p-4 border border-gray-200 rounded-md bg-gray-50">
                    <h3 className="font-medium text-gray-700 mb-2">Common JSON Errors:</h3>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Missing or extra commas between elements</li>
                      <li>Using single quotes instead of double quotes</li>
                      <li>Trailing commas in arrays or objects</li>
                      <li>Missing quotes around property names</li>
                      <li>Unescaped special characters in strings</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-3 gap-6 mt-8">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="text-blue-600 mb-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h3 className="font-medium text-gray-800">Instant Validation</h3>
              <p className="text-gray-600 text-sm mt-1">Check if your JSON is valid with detailed error reporting</p>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="text-blue-600 mb-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path>
                </svg>
              </div>
              <h3 className="font-medium text-gray-800">Pretty Formatting</h3>
              <p className="text-gray-600 text-sm mt-1">Beautify your JSON with customizable indentation</p>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="text-blue-600 mb-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"></path>
                </svg>
              </div>
              <h3 className="font-medium text-gray-800">Copy to Clipboard</h3>
              <p className="text-gray-600 text-sm mt-1">Quickly copy the validated and formatted JSON</p>
            </div>
          </div>
        </div>
      </main>
    </>


  );
};

export default JsonValidator;